/**
 * Status application, expiry and the poison tick (TRD-05 §7).
 *
 * Every duration is an ABSOLUTE tick number (R-05-27). The alternative — a countdown
 * decremented each tick — has to touch every status on every enemy every tick, and drifts
 * the moment a status is applied partway through a tick, because the application and the
 * decrement race for the same frame. An expiry tick is written once and only ever read.
 *
 * `baseSpeed` is never mutated (R-05-26). Effective speed is recomputed from it each
 * tick, so a slow that fails to clear leaves no trace: the next recompute overwrites it.
 * Mutating the base is how a chill becomes permanent, and the symptom shows up several
 * waves later as "the game got harder for no reason".
 */
import {
  CHILL_VULNERABILITY,
  HARD_CC_RECOVERY_TICKS,
  MARK_VULNERABILITY,
  POISON_DAMAGE_PER_STACK,
  POISON_MAX_STACKS,
  POISON_TICK_INTERVAL,
  SHOCK_MAX_STACKS,
  SHOCK_STUN_TICKS,
} from '../constants.js';
import type { Enemy } from '../entities.js';
import { INACTIVE, isStatusActive } from '../statuses.js';
import type { EntityId, StatusKind } from '../types.js';

export interface StatusApplication {
  status: StatusKind;
  /** Chill/wither slow fraction, 0..1. Ignored by boolean and stacking statuses. */
  magnitude: number;
  durationTicks: number;
  stacks: number;
  /** Who applied it. Poison keeps this for damage attribution; every other status
   *  ignores it, because their damage already carries a shooter. */
  sourceTower?: EntityId | undefined;
}

export interface StatusTuning {
  poisonMaxStacks: number;
  poisonTickInterval: number;
  /**
   * How much longer a Chill lasts — Frostbite's half of the bargain.
   *
   * DURATION, not cadence. Chill has no tick: it is a speed multiplier that expires, so
   * the `tickIntervalTicks` lever every stacking status uses does not exist for it. The
   * mutator was authored against that lever and therefore did nothing at all, which made
   * a Frostbite day the speed penalty with none of the compensation.
   */
  chillDurationMultiplier: number;
}

export const DEFAULT_STATUS_TUNING: StatusTuning = {
  poisonMaxStacks: POISON_MAX_STACKS,
  poisonTickInterval: POISON_TICK_INTERVAL,
  chillDurationMultiplier: 1,
};

/**
 * Apply one status to one enemy, following the stacking table in R-05-22.
 *
 * Returns the resulting stack count, which the caller emits in `statusApplied` so the
 * VFX layer can size the effect without reading world state.
 */
export function applyStatus(
  enemy: Enemy,
  application: StatusApplication,
  tick: number,
  tuning: StatusTuning = DEFAULT_STATUS_TUNING,
): number {
  // Immunity bounces the status entirely rather than zeroing it later: a stun that is
  // "applied but ignored" still shows a pip, still refreshes, and still reads to the
  // player as a stun that did nothing.
  if (application.status === 'stun' && enemy.immuneStun) return 0;
  if (application.status === 'freeze' && enemy.immuneFreeze) return 0;

  // Hard-CC recovery. An enemy that has just been held cannot be held again until the
  // window passes, whichever class is asking — see HARD_CC_RECOVERY_TICKS.
  if (isHardCc(application.status) && tick < enemy.ccImmuneUntilTick) return 0;

  const expiresAt = tick + application.durationTicks;
  const s = enemy.statuses;

  switch (application.status) {
    case 'poison': {
      const added = application.stacks > 0 ? application.stacks : 1;
      const wasActive = isStatusActive(s.poison.expiresAtTick, tick);
      s.poison.stacks = Math.min(tuning.poisonMaxStacks, (wasActive ? s.poison.stacks : 0) + added);
      // Every application refreshes the FULL duration, so sustained fire keeps a stack
      // alive indefinitely — that is what makes Venom a damage-over-time class rather
      // than a burst one.
      s.poison.expiresAtTick = expiresAt;
      // Newest applier is credited, matching how `lastHitBy` keeps the last shooter:
      // with several Venom towers stacking one enemy, the alternative is splitting a
      // tick of damage between towers that each contributed a whole stack.
      if (application.sourceTower !== undefined) s.poison.source = application.sourceTower;
      // The cadence only restarts when poison was absent. Re-arming it on every
      // application would let a fast tower postpone every tick forever and deal nothing.
      if (!wasActive || s.poison.nextTickAt === INACTIVE) {
        s.poison.nextTickAt = tick + tuning.poisonTickInterval;
      }
      return s.poison.stacks;
    }

    case 'chill': {
      // Strongest wins, but a weaker application still refreshes: a slow tower keeping a
      // strong chill topped up is a real strategy, and letting the weak hit overwrite the
      // magnitude would make it a downgrade instead (R-05-22, T-5).
      const active = isStatusActive(s.chill.expiresAtTick, tick);
      s.chill.magnitude = active
        ? Math.max(s.chill.magnitude, application.magnitude)
        : application.magnitude;
      // Tuned duration, and it must not shorten an existing chill: a retuned day that
      // let a fresh application expire earlier than the one it replaced would read as
      // the status flickering off under sustained fire.
      const chillExpiry =
        tick + Math.round(application.durationTicks * tuning.chillDurationMultiplier);
      s.chill.expiresAtTick = active ? Math.max(s.chill.expiresAtTick, chillExpiry) : chillExpiry;
      return 1;
    }

    case 'shock': {
      const active = isStatusActive(s.shock.expiresAtTick, tick);
      const added = application.stacks > 0 ? application.stacks : 1;
      const stacks = (active ? s.shock.stacks : 0) + added;
      if (stacks >= SHOCK_MAX_STACKS) {
        // Consumed in full — a 4th application while stunned starts a fresh stack from
        // zero rather than topping up a spent counter (T-6).
        s.shock.stacks = 0;
        s.shock.expiresAtTick = INACTIVE;
        // ...but the payout is a STUN, so it owes the same two checks the top of this
        // function makes, and for a long time it made neither. The immunity guard above
        // only ever sees `status: 'shock'`, which is how a shock-stacking board held a
        // stun-immune boss in place; and nothing rate-limited the payout, which is how
        // the same board held EVERYTHING in place. The stacks are still spent either way
        // — the burst happened, it simply does not land.
        if (!enemy.immuneStun && tick >= enemy.ccImmuneUntilTick) {
          s.stun.expiresAtTick = Math.max(s.stun.expiresAtTick, tick + SHOCK_STUN_TICKS);
          armRecovery(enemy, s.stun.expiresAtTick);
        }
        return 0;
      }
      s.shock.stacks = stacks;
      s.shock.expiresAtTick = expiresAt;
      return stacks;
    }

    case 'wither': {
      const active = isStatusActive(s.wither.expiresAtTick, tick);
      s.wither.magnitude = active
        ? Math.max(s.wither.magnitude, application.magnitude)
        : application.magnitude;
      s.wither.expiresAtTick = expiresAt;
      return 1;
    }

    case 'mark':
      s.mark.expiresAtTick = expiresAt;
      return 1;

    // Longest remaining wins, so a short stun cannot cut a long freeze short.
    case 'stun':
      s.stun.expiresAtTick = Math.max(s.stun.expiresAtTick, expiresAt);
      armRecovery(enemy, s.stun.expiresAtTick);
      return 1;

    case 'freeze':
      s.freeze.expiresAtTick = Math.max(s.freeze.expiresAtTick, expiresAt);
      armRecovery(enemy, s.freeze.expiresAtTick);
      return 1;
  }
}

/** The two statuses that stop an enemy dead, and therefore share one recovery window. */
function isHardCc(status: StatusKind): boolean {
  return status === 'stun' || status === 'freeze';
}

/**
 * Start the recovery window, measured from when the hold ENDS rather than from now.
 *
 * From `now` would let a long stun be chained by a short one the instant the window
 * elapsed underneath it, which is the bug wearing a different hat.
 */
function armRecovery(enemy: Enemy, holdEndsAt: number): void {
  enemy.ccImmuneUntilTick = Math.max(enemy.ccImmuneUntilTick, holdEndsAt + HARD_CC_RECOVERY_TICKS);
}

/** R-05-25 — held enemies do not move and cannot be healed, but can still be hit. */
export function isImmobilised(enemy: Enemy, tick: number): boolean {
  return (
    isStatusActive(enemy.statuses.stun.expiresAtTick, tick) ||
    isStatusActive(enemy.statuses.freeze.expiresAtTick, tick)
  );
}

/** R-05-26. Recomputed from base every tick; clamped so a >100% chill cannot reverse it. */
export function effectiveSpeed(enemy: Enemy, tick: number): number {
  if (isImmobilised(enemy, tick)) return 0;
  const chill = isStatusActive(enemy.statuses.chill.expiresAtTick, tick)
    ? enemy.statuses.chill.magnitude
    : 0;
  const speed = enemy.baseSpeed * (1 - chill);
  return speed > 0 ? speed : 0;
}

/** Damage stage 5 (R-05-18 pipeline). Chill and Mark compound — both are conditional. */
export function vulnerabilityMultiplier(enemy: Enemy, tick: number): number {
  let multiplier = 1;
  if (isStatusActive(enemy.statuses.chill.expiresAtTick, tick)) multiplier *= CHILL_VULNERABILITY;
  if (isStatusActive(enemy.statuses.mark.expiresAtTick, tick)) multiplier *= MARK_VULNERABILITY;
  return multiplier;
}

/**
 * Clear anything whose expiry has passed, and report the poison damage owed this tick.
 *
 * Returns 0 when no poison tick is due. The caller applies it as `true` damage (R-05-24)
 * rather than doing it here, so that all damage — poison included — goes through the one
 * pipeline that owns the death check.
 */
export function tickStatuses(
  enemy: Enemy,
  tick: number,
  tuning: StatusTuning = DEFAULT_STATUS_TUNING,
): number {
  const s = enemy.statuses;

  if (s.chill.expiresAtTick !== INACTIVE && !isStatusActive(s.chill.expiresAtTick, tick)) {
    s.chill.expiresAtTick = INACTIVE;
    s.chill.magnitude = 0;
  }
  if (s.wither.expiresAtTick !== INACTIVE && !isStatusActive(s.wither.expiresAtTick, tick)) {
    s.wither.expiresAtTick = INACTIVE;
    s.wither.magnitude = 0;
  }
  if (s.shock.expiresAtTick !== INACTIVE && !isStatusActive(s.shock.expiresAtTick, tick)) {
    s.shock.expiresAtTick = INACTIVE;
    s.shock.stacks = 0;
  }
  if (s.mark.expiresAtTick !== INACTIVE && !isStatusActive(s.mark.expiresAtTick, tick)) {
    s.mark.expiresAtTick = INACTIVE;
  }
  if (s.stun.expiresAtTick !== INACTIVE && !isStatusActive(s.stun.expiresAtTick, tick)) {
    s.stun.expiresAtTick = INACTIVE;
  }
  if (s.freeze.expiresAtTick !== INACTIVE && !isStatusActive(s.freeze.expiresAtTick, tick)) {
    s.freeze.expiresAtTick = INACTIVE;
  }

  if (s.poison.expiresAtTick !== INACTIVE && !isStatusActive(s.poison.expiresAtTick, tick)) {
    s.poison.stacks = 0;
    s.poison.expiresAtTick = INACTIVE;
    s.poison.nextTickAt = INACTIVE;
    return 0;
  }

  if (s.poison.stacks <= 0 || s.poison.nextTickAt === INACTIVE || tick < s.poison.nextTickAt) {
    return 0;
  }

  // Advanced from the scheduled tick, not from `tick`, so the cadence stays on its
  // original phase even if a tick is processed late. Absolute scheduling is the whole
  // point of R-05-23.
  s.poison.nextTickAt += tuning.poisonTickInterval;
  return s.poison.stacks * 2 * POISON_DAMAGE_PER_STACK;
}
